/** VISTOERA_GENERATE_VIDEO_IMAGE_ASSET_THUMBNAIL_HOVER_PREVIEW_PRIMITIVE_P1_V1
 * Canonical IMAGE-asset thumbnail hover enlarged preview — shared by every
 * Canvas function node that shows a small image chip (Generate Video shot
 * references, keyframe thumbs, Input Materials IMAGE chips, Try-On slots,
 * Storyboard/Script reference thumbs, …). NOT video/audio/text.
 *
 * Contract (§P4–§P8/§P16 + VistoeraTASK-00742 / 00743):
 *  - ~300ms open delay; immediate close on pointer/focus leave.
 *  - Portal + `position:fixed` viewport coords → canvas zoom/pan independent.
 *  - Collision-aware placement (prefer right of thumb, flip left, clamp Y).
 *  - Overlay is view-only (`pointer-events: none`) — never blocks ×/drag.
 *  - Primary button pointerdown closes preview immediately (before HTML5
 *    dragstart). Parent chips often own `draggable`; after a prior drag some
 *    browsers start the next drag without delivering child pointerdown, so we
 *    ALSO close on the nearest draggable ancestor's dragstart (capture).
 *  - Lock/unlock uses refs + synchronous window listeners (never wait for a
 *    useEffect attach) so rapid pointerup/dragend cannot miss unlock.
 *  - While locked, hover cannot reopen; unlock does not auto-flash open.
 *  - Preview src prefers `previewUrl` (web/display variant); falls back to
 *    `thumbUrl` only when preview fails or was not supplied.
 *  - Zero backend/metadata fetches — callers supply URLs up front. */

import { PictureOutlined } from '@ant-design/icons'
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useCanvasAnchoredReposition } from '../canvasAnchoredOverlay'

export const CANVAS_IMAGE_HOVER_PREVIEW_OPEN_DELAY_MS = 300
export const CANVAS_IMAGE_HOVER_PREVIEW_MAX_WIDTH_PX = 420
export const CANVAS_IMAGE_HOVER_PREVIEW_MAX_HEIGHT_PX = 520

const GAP_PX = 10
const VIEWPORT_MARGIN_PX = 12

interface Props {
  thumbUrl: string | null
  /** Larger variant for the hover overlay (§P14). Defaults to `thumbUrl`. */
  previewUrl?: string | null
  alt?: string
  className?: string
  fallback?: ReactNode
  /** When true, hover preview is suppressed (e.g. parent section collapsed). */
  disabled?: boolean
  /** When true, the trigger is keyboard-focusable (omit inside `<button>` parents). */
  focusable?: boolean
}

/**
 * Pure lock/unlock helpers exported for consecutive-interaction contract tests
 * (VistoeraTASK-00743). Mirrors the component's sync window-listener strategy.
 */
export type HoverPreviewInteractionGate = {
  isLocked: () => boolean
  lock: (closePreview: () => void) => void
  unlock: () => void
  dispose: () => void
}

export function createHoverPreviewInteractionGate(
  addWindowListener: typeof window.addEventListener = window.addEventListener.bind(window),
  removeWindowListener: typeof window.removeEventListener = window.removeEventListener.bind(window),
): HoverPreviewInteractionGate {
  let locked = false
  let onEnd: (() => void) | null = null

  const detachEndListeners = () => {
    if (!onEnd) return
    removeWindowListener('pointerup', onEnd, true)
    removeWindowListener('pointercancel', onEnd, true)
    removeWindowListener('dragend', onEnd, true)
    onEnd = null
  }

  const unlock = () => {
    locked = false
    detachEndListeners()
  }

  const lock = (closePreview: () => void) => {
    closePreview()
    if (locked) return
    locked = true
    onEnd = () => unlock()
    addWindowListener('pointerup', onEnd, true)
    addWindowListener('pointercancel', onEnd, true)
    addWindowListener('dragend', onEnd, true)
  }

  return {
    isLocked: () => locked,
    lock,
    unlock,
    dispose: () => unlock(),
  }
}

export default function ImageThumbnailHoverPreview({
  thumbUrl,
  previewUrl,
  alt = '',
  className = 'pcv-gen-field-thumb',
  fallback,
  disabled,
  focusable = false,
}: Props) {
  const { t } = useTranslation('canvas')
  const triggerRef = useRef<HTMLSpanElement | null>(null)
  const popRef = useRef<HTMLDivElement | null>(null)
  const openTimerRef = useRef(0)
  const gateRef = useRef<HoverPreviewInteractionGate | null>(null)
  if (!gateRef.current) gateRef.current = createHoverPreviewInteractionGate()
  const [open, setOpen] = useState(false)
  const [imgLoaded, setImgLoaded] = useState(false)
  const [useThumbFallback, setUseThumbFallback] = useState(false)
  const [imgFailed, setImgFailed] = useState(false)
  const [style, setStyle] = useState<{ top: number; left: number } | null>(null)

  const primaryPreview = previewUrl || thumbUrl
  const displaySrc =
    useThumbFallback && thumbUrl && primaryPreview !== thumbUrl ? thumbUrl : primaryPreview

  const close = useCallback(() => {
    window.clearTimeout(openTimerRef.current)
    openTimerRef.current = 0
    setOpen(false)
    setImgLoaded(false)
    setUseThumbFallback(false)
    setImgFailed(false)
    setStyle(null)
  }, [])

  const scheduleOpen = useCallback(() => {
    if (disabled || !thumbUrl || !primaryPreview) return
    if (gateRef.current?.isLocked()) return
    window.clearTimeout(openTimerRef.current)
    openTimerRef.current = window.setTimeout(() => {
      if (gateRef.current?.isLocked()) return
      setOpen(true)
    }, CANVAS_IMAGE_HOVER_PREVIEW_OPEN_DELAY_MS)
  }, [disabled, thumbUrl, primaryPreview])

  const cancelOpen = useCallback(() => {
    window.clearTimeout(openTimerRef.current)
    openTimerRef.current = 0
    close()
  }, [close])

  const lockInteraction = useCallback(() => {
    gateRef.current?.lock(close)
  }, [close])

  // Native capture listeners: survive parent-owned HTML5 drag where the 2nd+
  // dragstart may skip React child pointerdown, and avoid useEffect attach races.
  useEffect(() => {
    const trigger = triggerRef.current
    if (!trigger) return

    const onPointerDownCapture = (e: PointerEvent) => {
      if (e.button !== 0) return
      lockInteraction()
    }

    const onDragStartCapture = () => {
      lockInteraction()
    }

    trigger.addEventListener('pointerdown', onPointerDownCapture, true)
    trigger.addEventListener('dragstart', onDragStartCapture, true)

    const dragAncestor = trigger.closest('[draggable="true"]')
    dragAncestor?.addEventListener('dragstart', onDragStartCapture, true)

    return () => {
      trigger.removeEventListener('pointerdown', onPointerDownCapture, true)
      trigger.removeEventListener('dragstart', onDragStartCapture, true)
      dragAncestor?.removeEventListener('dragstart', onDragStartCapture, true)
    }
  }, [lockInteraction, thumbUrl])

  useLayoutEffect(() => {
    if (!open) return
    const reposition = () => {
      const trigger = triggerRef.current
      const pop = popRef.current
      if (!trigger) return
      const rect = trigger.getBoundingClientRect()
      const popW = pop?.offsetWidth ?? CANVAS_IMAGE_HOVER_PREVIEW_MAX_WIDTH_PX + 12
      const popH = pop?.offsetHeight ?? CANVAS_IMAGE_HOVER_PREVIEW_MAX_HEIGHT_PX + 12
      let left = rect.right + GAP_PX
      if (left + popW > window.innerWidth - VIEWPORT_MARGIN_PX) {
        left = rect.left - GAP_PX - popW
      }
      if (left < VIEWPORT_MARGIN_PX) left = VIEWPORT_MARGIN_PX
      let top = rect.top + rect.height / 2 - popH / 2
      top = Math.max(VIEWPORT_MARGIN_PX, Math.min(top, window.innerHeight - popH - VIEWPORT_MARGIN_PX))
      setStyle({ top, left })
    }
    reposition()
    window.addEventListener('resize', reposition)
    window.addEventListener('scroll', reposition, true)
    return () => {
      window.removeEventListener('resize', reposition)
      window.removeEventListener('scroll', reposition, true)
    }
  }, [open, imgLoaded])

  useCanvasAnchoredReposition(open, () => {
    const trigger = triggerRef.current
    const pop = popRef.current
    if (!trigger) return
    const rect = trigger.getBoundingClientRect()
    const popW = pop?.offsetWidth ?? CANVAS_IMAGE_HOVER_PREVIEW_MAX_WIDTH_PX + 12
    const popH = pop?.offsetHeight ?? CANVAS_IMAGE_HOVER_PREVIEW_MAX_HEIGHT_PX + 12
    let left = rect.right + GAP_PX
    if (left + popW > window.innerWidth - VIEWPORT_MARGIN_PX) {
      left = rect.left - GAP_PX - popW
    }
    if (left < VIEWPORT_MARGIN_PX) left = VIEWPORT_MARGIN_PX
    let top = rect.top + rect.height / 2 - popH / 2
    top = Math.max(VIEWPORT_MARGIN_PX, Math.min(top, window.innerHeight - popH - VIEWPORT_MARGIN_PX))
    setStyle({ top, left })
  })

  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    document.addEventListener('keydown', onKeyDown, true)
    return () => document.removeEventListener('keydown', onKeyDown, true)
  }, [open, close])

  useEffect(
    () => () => {
      window.clearTimeout(openTimerRef.current)
      gateRef.current?.dispose()
    },
    [],
  )

  if (!thumbUrl) return fallback ?? <PictureOutlined />

  const portalContainer = triggerRef.current?.closest('.pcv-root') || document.body

  return (
    <>
      <span
        ref={triggerRef}
        className="pcv-image-thumbnail-hover-preview-trigger"
        onPointerEnter={scheduleOpen}
        onPointerLeave={cancelOpen}
        onFocus={scheduleOpen}
        onBlur={cancelOpen}
        tabIndex={focusable ? 0 : -1}
        role="img"
        aria-label={alt || undefined}
      >
        <img src={thumbUrl} alt={alt} className={`${className} nodrag nopan`} draggable={false} />
      </span>
      {open && displaySrc
        ? createPortal(
            <div
              ref={popRef}
              className="pcv-image-thumbnail-hover-preview-popover nodrag nowheel"
              style={{
                top: style?.top ?? -9999,
                left: style?.left ?? -9999,
                visibility: style ? 'visible' : 'hidden',
              }}
              aria-hidden="true"
            >
              {!imgFailed ? (
                <>
                  {!imgLoaded ? (
                    <span className="pcv-image-thumbnail-hover-preview-loading">{t('imageThumbnailHoverPreview.loading')}</span>
                  ) : null}
                  <img
                    src={displaySrc}
                    alt=""
                    className={`pcv-image-thumbnail-hover-preview-img${imgLoaded ? ' is-loaded' : ''}`}
                    draggable={false}
                    onLoad={() => setImgLoaded(true)}
                    onError={() => {
                      if (!useThumbFallback && thumbUrl && displaySrc !== thumbUrl) {
                        setUseThumbFallback(true)
                        setImgLoaded(false)
                        return
                      }
                      setImgFailed(true)
                    }}
                  />
                </>
              ) : (
                <span className="pcv-image-thumbnail-hover-preview-error">{t('imageThumbnailHoverPreview.failed')}</span>
              )}
            </div>,
            portalContainer,
          )
        : null}
    </>
  )
}
